/**
 * DEV-ONLY handoff detail fixtures (CT-5096) — opt-in via localStorage.
 *
 * Real HITL rows from `POST /v1/tasks/hitl/list` map only what LangGraph puts in
 * `render`. To preview the full EVT-021 body against a live contract (e.g.
 * wq5096-handoff-001) without mixing mock into production-like sessions:
 *
 *   Enable :  localStorage.setItem('wq_enrich_handoff', '1')   // then reload
 *   Disable:  localStorage.removeItem('wq_enrich_handoff')
 *
 * Gated to `import.meta.env.DEV` — never runs in production builds.
 */

import { mergeFixtureDetail } from './fixtureMerge.js';

export const HANDOFF_FIXTURE_FLAG = 'wq_enrich_handoff';

const HANDOFF_KEY_RULES = { packet: 'mergeObject', trace: 'replaceIfEmpty' };

export const HANDOFF_DETAIL_FIXTURES = {
  'wq5096-handoff-001': {
    workflow: 'AdvisorCopilot Lead Qualification',
    entity: 'Jordan Ellis — Northfield Capital Partners',
    recordId: 'LEAD-8834',
    source: 'Web Inquiry Form',
    handoffJustification:
      'Lead score 94/100 exceeds the 90-point auto-handoff threshold',
    traceRef: 'WF-4410',
    packet: {
      keyFacts: [
        '$12M portfolio under management',
        'Requested callback within 24 hours',
        'Expressed dissatisfaction with current advisors',
      ],
      recommendations: [
        'Lead with responsiveness — prospect cited slow response times from current advisor',
        'Reference competitive fee structures for portfolios over $10M',
      ],
      crmRecord: 'CRM-99213',
      knowledgeContract: 'KC-ADVISORY-ONBOARDING',
    },
    agentSummary:
      'Prospect asked about portfolio rebalancing fees and advisor availability; agent answered fee questions and offered to schedule a call with a human advisor.',
    trace: [
      {
        label: 'Lead Captured',
        description: 'Inbound inquiry captured from the web form',
        timestamp: '02:00 AM',
        state: 'done',
      },
      {
        label: 'Qualification Scoring',
        description: 'Agent scored the lead 94/100',
        timestamp: '02:05 AM',
        state: 'done',
      },
      {
        label: 'Threshold Check',
        description: 'Score exceeds the 90-point auto-handoff threshold',
        timestamp: '02:06 AM',
        state: 'done',
      },
      {
        label: 'HTL Handoff',
        description: 'Ownership transferred — agent will not continue',
        timestamp: '02:06 AM',
        state: 'paused',
      },
    ],
  },
};

/** True only in dev with `localStorage.wq_enrich_handoff === '1'`. */
export function isHandoffFixtureEnabled() {
  if (!import.meta.env.DEV) return false;
  try {
    return localStorage.getItem(HANDOFF_FIXTURE_FLAG) === '1';
  } catch {
    return false;
  }
}

/** Fill missing handoff detail from a known fixture; live render values win. */
export function mergeHandoffDetailFixture(contractId, detail = {}) {
  const fixture = HANDOFF_DETAIL_FIXTURES[contractId];
  if (!fixture) return detail;
  return mergeFixtureDetail(detail, fixture, HANDOFF_KEY_RULES);
}

/** No-op unless {@link isHandoffFixtureEnabled} — keeps live sessions honest. */
export function applyHandoffDetailFixture(contractId, detail = {}) {
  if (!isHandoffFixtureEnabled()) return detail;
  return mergeHandoffDetailFixture(contractId, detail);
}
